import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="microphone-selector"
export default class extends Controller {
  static targets = ["select"]

  async connect() {
    try {
      // LocalStorageから保存されたデバイス情報を取得
      const savedDevice = this.getSavedDeviceInfo()

      if (savedDevice) {
        // LocalStorageにデータがある場合: 保存された情報を表示（getUserMedia()なし）
        this.displaySavedDevice(savedDevice)
      } else {
        // LocalStorageが空の場合: 初回のみgetUserMedia()を呼んで取得
        await this.initializeFirstTime()
      }

      // セレクトボックスがクリックされた時に最新のデバイス一覧を取得
      if (this.hasSelectTarget) {
        this.selectTarget.addEventListener('focus', () => this.refreshDeviceList())
      }

    } catch (error) {
      console.error("マイクアクセス権限エラー:", error)
      this.showError("マイクへのアクセス権限が必要です")
    }
  }

  async initializeFirstTime() {
    try {
      // getUserMedia()でデバイス一覧を取得
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(track => track.stop())

      // デバイス一覧を取得
      await this.loadMicrophones()

      // 最初のマイクを自動選択
      await this.selectFirstMicrophone()
    } catch (error) {
      console.error('初回マイク取得エラー:', error)
      throw error
    }
  }

  async refreshDeviceList() {
    try {
      // 最新のデバイス一覧を取得（許諾済みなのでポップアップなし）
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(track => track.stop())

      // デバイス一覧を更新
      await this.loadMicrophones()

      console.log('デバイス一覧を更新しました')
    } catch (error) {
      console.error('デバイス一覧更新エラー:', error)
    }
  }

  displaySavedDevice(savedDevice) {
    if (!this.hasSelectTarget) return

    // セレクトボックスに保存されたデバイスを表示
    const option = document.createElement('option')
    option.value = savedDevice.deviceId
    option.textContent = savedDevice.label
    this.selectTarget.appendChild(option)
    this.selectTarget.value = savedDevice.deviceId

    console.log('LocalStorageからマイク情報を復元:', savedDevice.label)
  }

  async selectFirstMicrophone() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(device => device.kind === 'audioinput')

      if (audioInputs.length > 0) {
        const firstMic = audioInputs[0]

        // デバイスIDと名前をLocalStorageに保存
        this.saveDeviceInfo({
          deviceId: firstMic.deviceId,
          label: firstMic.label || `マイク 1`
        })

        // 後方互換性のため、古いキーも保存
        this.saveSelectedDevice(firstMic.deviceId)

        // セレクトボックスの値を確実に更新
        if (this.hasSelectTarget) {
          const option = this.selectTarget.querySelector(`option[value="${firstMic.deviceId}"]`)
          if (option) {
            this.selectTarget.value = firstMic.deviceId
          }
        }

        console.log('デフォルトマイクを自動選択:', firstMic.label || firstMic.deviceId)
      }
    } catch (error) {
      console.error('マイク自動選択エラー:', error)
      throw error
    }
  }

  hasDetailedDeviceInfo() {
    // デバイスにlabelがあるかチェック（権限がある場合はlabelが取得できる）
    const devices = this.lastDevices || []
    return devices.some(device => device.kind === 'audioinput' && device.label !== '')
  }

  async loadMicrophones() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      this.lastDevices = devices // 権限チェック用に保存
      
      const audioInputs = devices.filter(device => device.kind === 'audioinput')
      
      if (!this.hasSelectTarget) return
      
      // 更新が必要かチェック
      if (!this.shouldUpdateOptions(audioInputs)) {
        return
      }
      
      // セレクトボックスを更新
      this.updateSelectOptions(audioInputs)
      
    } catch (error) {
      console.error("デバイス一覧取得エラー:", error)
      this.showError("マイクデバイスの取得に失敗しました")
    }
  }
  
  shouldUpdateOptions(audioInputs) {
    const currentOptions = Array.from(this.selectTarget.options)
    return this.needsUpdate(currentOptions, audioInputs)
  }
  
  updateSelectOptions(audioInputs) {
    // 現在の値を保存
    const currentValue = this.selectTarget.value
    
    // 新しいオプションを作成
    const fragment = this.createOptionsFragment(audioInputs)
    
    // セレクトボックスを更新
    this.replaceSelectOptions(fragment)
    
    // 値を復元
    this.restoreSelectedValue(currentValue)
  }
  
  createOptionsFragment(audioInputs) {
    const fragment = document.createDocumentFragment()
    
    // デフォルトオプション
    const defaultOption = document.createElement('option')
    defaultOption.value = ''
    defaultOption.textContent = 'マイクを選択してください'
    fragment.appendChild(defaultOption)
    
    // マイクデバイスを追加
    audioInputs.forEach((device, index) => {
      const option = document.createElement('option')
      option.value = device.deviceId
      option.textContent = device.label || `マイク ${index + 1}`
      fragment.appendChild(option)
    })
    
    return fragment
  }
  
  replaceSelectOptions(fragment) {
    this.selectTarget.innerHTML = ''
    this.selectTarget.appendChild(fragment)
    this.selectTarget.disabled = false
  }
  
  restoreSelectedValue(currentValue) {
    // LocalStorageから保存された値も取得
    const savedDeviceId = this.getSelectedDevice()
    const valueToRestore = currentValue || savedDeviceId
    
    if (valueToRestore && this.selectTarget.querySelector(`option[value="${valueToRestore}"]`)) {
      this.selectTarget.value = valueToRestore
    }
  }

  needsUpdate(currentOptions, newDevices) {
    // 初期状態（空）の場合は更新が必要
    if (currentOptions.length === 0) return true
    
    // 初期状態（デフォルトオプションのみ）の場合は更新が必要
    if (currentOptions.length <= 1) return true
    
    // デバイス数が変わった場合は更新が必要（デフォルトオプションを除く）
    const DEFAULT_OPTION_COUNT = 1
    if (currentOptions.length - DEFAULT_OPTION_COUNT !== newDevices.length) return true
    
    // デバイスIDが変わった場合は更新が必要（Setを使用してO(n)で比較）
    const currentIds = new Set(currentOptions.slice(1).map(opt => opt.value))
    const newIds = new Set(newDevices.map(device => device.deviceId))
    
    // サイズが異なる場合は変更あり
    if (currentIds.size !== newIds.size) return true
    
    // すべてのIDが一致するかチェック
    for (const id of newIds) {
      if (!currentIds.has(id)) return true
    }
    
    return false
  }

  change() {
    const deviceId = this.selectTarget.value
    const deviceLabel = this.selectTarget.options[this.selectTarget.selectedIndex]?.text

    // デバイスIDと名前の両方を保存
    if (deviceId && deviceLabel) {
      this.saveDeviceInfo({
        deviceId: deviceId,
        label: deviceLabel
      })
    } else {
      this.clearDeviceInfo()
    }

    // 後方互換性のため、古いキーも保存
    this.saveSelectedDevice(deviceId)
  }

  // LocalStorage操作（新しい方法: デバイス情報をJSON保存）
  saveDeviceInfo(deviceInfo) {
    if (deviceInfo && deviceInfo.deviceId) {
      localStorage.setItem('selectedMicrophoneInfo', JSON.stringify(deviceInfo))
    } else {
      this.clearDeviceInfo()
    }
  }

  getSavedDeviceInfo() {
    const saved = localStorage.getItem('selectedMicrophoneInfo')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch (error) {
        console.error('LocalStorage解析エラー:', error)
        return null
      }
    }
    return null
  }

  clearDeviceInfo() {
    localStorage.removeItem('selectedMicrophoneInfo')
    localStorage.removeItem('selectedMicrophoneId')
  }

  // 後方互換性のため、古いメソッドも残す
  saveSelectedDevice(deviceId) {
    if (deviceId) {
      localStorage.setItem('selectedMicrophoneId', deviceId)
    } else {
      localStorage.removeItem('selectedMicrophoneId')
    }
  }

  getSelectedDevice() {
    return localStorage.getItem('selectedMicrophoneId')
  }

  showError(message) {
    // エラー表示（必要に応じてUIに反映）
    console.error(message)
    
    if (this.hasSelectTarget) {
      this.selectTarget.innerHTML = '<option value="">エラー: ' + message + '</option>'
      this.selectTarget.disabled = true
    }
  }
}